import React from 'react';
import { Form, Popconfirm, Select, Alert } from 'antd';
import { DeleteOutlined, MailOutlined } from '@ant-design/icons';
import PermissionSelector from '../../../components/PermissionSelector';
import { useStore } from '../../../hooks/storeContext';
import {
  ActionButton,
  StyledInput,
  StyledSelect,
  ResponsiveModal
} from '../styled';

const { Option } = Select;

const UserFormModal = ({
  visible,
  editingUser,
  form,
  selectedPermissions,
  setSelectedPermissions,
  selectedRole,
  setSelectedRole,
  onOk,
  onCancel,
  onDeleteUser,
  onSendPasswordReset,
  isSelf = false
}) => {
  // Non-admins can only give out the permissions they hold (the API enforces this).
  const { authStore } = useStore();
  const actorIsAdmin = authStore.isAdmin;
  const grantable = actorIsAdmin ? null : (authStore.me?.permissions || []).slice();

  return (
    <ResponsiveModal
      title={editingUser ? 'Edit User' : 'Add New User'}
      open={visible}
      onCancel={onCancel}
      footer={[
        <div
          key="footer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            {editingUser && !isSelf && (
              <Popconfirm
                title="Are you sure you want to delete this user?"
                onConfirm={() => {
                  onDeleteUser(editingUser._id);
                  onCancel();
                }}
                okText="Yes"
                cancelText="No"
              >
                <ActionButton
                  danger
                  icon={<DeleteOutlined />}
                  style={{
                    color: '#ff4d4f',
                    borderColor: '#ff4d4f'
                  }}
                >
                  Delete
                </ActionButton>
              </Popconfirm>
            )}
            {editingUser && (
              <ActionButton
                icon={<MailOutlined />}
                onClick={() => onSendPasswordReset(editingUser._id)}
              >
                Reset Password
              </ActionButton>
            )}
          </div>

          <ActionButton type="primary" onClick={onOk}>
            {editingUser ? 'Update' : 'Create'} User
          </ActionButton>
        </div>
      ]}
      width={600}
    >
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ 
          margin: '0 0 16px 0', 
          fontSize: '16px', 
          fontWeight: '600', 
          color: '#262626',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: '8px',
          textAlign: 'left'
        }}>
          User Information
        </h3>
        {isSelf && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="You're editing your own account"
            description="Role, permissions, and status are read-only here. Ask another admin to make those changes to avoid locking yourself out."
          />
        )}
        <Form
          form={form}
          layout="vertical"
          name="userForm"
          initialValues={{
            status: 'active'
          }}
        >
          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter full name' }]}
          >
            <StyledInput placeholder="Enter full name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' }
            ]}
          >
            <StyledInput placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
          >
            <StyledInput placeholder="Enter phone number" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <StyledSelect 
              placeholder="Select user role"
              onChange={(value) => setSelectedRole(value)}
              disabled={isSelf}
            >
              {(actorIsAdmin || selectedRole === 'admin') && (
                <Option value="admin" disabled={!actorIsAdmin}>Admin</Option>
              )}
              <Option value="user">User</Option>
              <Option value="volunteer">Volunteer</Option>
            </StyledSelect>
          </Form.Item>

          <Form.Item
            name="permissions"
            label="Permissions"
          >
            <PermissionSelector
              value={selectedPermissions}
              onChange={setSelectedPermissions}
              role={selectedRole}
              disabled={isSelf}
              grantable={grantable}
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <StyledSelect placeholder="Select user status" disabled={isSelf}>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </StyledSelect>
          </Form.Item>
        </Form>
      </div>
    </ResponsiveModal>
  );
};

export default UserFormModal;
